import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { Observable, tap } from 'rxjs';

import { ApiListResponse, ApiResponse } from '@/types/api-response.model';
import {
  CreateLeaveDto,
  LeaveBalance,
  LeaveData,
  LeaveTypes,
} from '@/types/leave.interfaces';

@Injectable({ providedIn: 'root' })
export class LeaveService {
  private http = inject(HttpClient);
  private readonly apiUrl = '/api/leaves';

  private readonly _leaves = signal<LeaveData[]>([]);
  readonly leaves = this._leaves.asReadonly();

  readonly activeLeaves = computed(() =>
    this._leaves().filter(
      l => l.status === 'pending' || l.status === 'approved'
    )
  );

  refresh(): Observable<ApiListResponse<LeaveData>> {
    return this.getLeaves().pipe(
      tap(res => this._leaves.set(res.data ?? []))
    );
  }

  getLeaves(params?: {
    page?: number;
    size?: number;
    status?: string;
    employeeId?: string;
  }): Observable<ApiListResponse<LeaveData>> {
    let query = `page=${params?.page ?? 1}&size=${params?.size ?? 1000}`;

    if (params?.status) {
      query += `&status=${params.status}`;
    }
    if (params?.employeeId) {
      query += `&employeeId=${params.employeeId}`;
    }

    return this.http.get<ApiListResponse<LeaveData>>(`${this.apiUrl}?${query}`);
  }

  getLeaveById(id: string): Observable<ApiResponse<LeaveData>> {
    return this.http.get<ApiResponse<LeaveData>>(`${this.apiUrl}/${id}`);
  }

  getLeaveBalance(employeeId: string): Observable<ApiResponse<LeaveBalance>> {
    return this.http.get<ApiResponse<LeaveBalance>>(
      `${this.apiUrl}/balance/${employeeId}`
    );
  }

  getLeaveTypes(employeeId: string): Observable<ApiListResponse<LeaveTypes>> {
    return this.http.get<ApiListResponse<LeaveTypes>>(
      `${this.apiUrl}/types/${employeeId}`
    );
  }

  getLeaveHistory(employeeId: string): Observable<ApiListResponse<any>> {
    return this.http.get<ApiListResponse<any>>(
      `${this.apiUrl}/history/${employeeId}`
    );
  }

  getEmployees(): Observable<ApiListResponse<any>> {
    return this.http.get<ApiListResponse<any>>(
      `/api/employee?page=1&size=1000&isActive=true`
    );
  }

  updateLeaveType(
    id: string,
    leaveType: string,
    remaining: number,
    reason: string
  ): Observable<ApiResponse<any>> {
    return this.http.patch<ApiResponse<any>>(
      `${this.apiUrl}/settings/${id}`,
      {
        //leaveType
        remaining,
        reason,
      }
    );
  }

  createLeave(
    payload: CreateLeaveDto
  ): Observable<ApiResponse<LeaveData>> {
    return this.http
      .post<ApiResponse<LeaveData>>(this.apiUrl, payload)
      .pipe(
        tap(res => {
          if (res.data) {
            this._leaves.update(list => [res.data, ...list]);
          }
        })
      );
  }

  createLeaveByAdmin(
    payload: CreateLeaveDto
  ): Observable<ApiResponse<LeaveData>> {
    return this.http
      .post<ApiResponse<LeaveData>>(
        `${this.apiUrl}/admin-create-request`,
        payload
      )
      .pipe(
        tap(res => {
          if (res.data) {
            this._leaves.update(list => [res.data, ...list]);
          }
        })
      );
  }

  updateLeave(
    id: string,
    payload: Partial<CreateLeaveDto>
  ): Observable<ApiResponse<LeaveData>> {
    return this.http
      .patch<ApiResponse<LeaveData>>(`${this.apiUrl}/${id}`, payload)
      .pipe(
        tap(res => {
          this._leaves.update(list =>
            list.map(l =>
              l.id === id && res.data ? { ...l, ...res.data } : l
            )
          );
        })
      );
  }

  approveLeave(id: string): Observable<ApiResponse<LeaveData>> {
    return this.http
      .patch<ApiResponse<LeaveData>>(
        `${this.apiUrl}/${id}/approve`,
        {}
      )
      .pipe(
        tap(res => {
          this._leaves.update(list =>
            list.map(l =>
              l.id === id
                ? { ...l, ...(res.data ?? {}), status: 'approved' }
                : l
            )
          );
        })
      );
  }

  rejectLeave(id: string): Observable<ApiResponse<LeaveData>> {
    return this.http
      .patch<ApiResponse<LeaveData>>(
        `${this.apiUrl}/${id}/reject`,
        {}
      )
      .pipe(
        tap(res => {
          this._leaves.update(list =>
            list.map(l =>
              l.id === id
                ? { ...l, ...(res.data ?? {}), status: 'rejected' }
                : l
            )
          );
        })
      );
  }

  cancelLeave(id: string): Observable<ApiResponse<LeaveData>> {
    return this.http
      .patch<ApiResponse<LeaveData>>(
        `${this.apiUrl}/${id}/cancel`,
        {}
      )
      .pipe(
        tap(() => {
          this._leaves.update(list =>
            list.filter(l => l.id !== id)
          );
        })
      );
  }
}
